import {
    Alert,
    Anchor,
    Button,
    Group,
    Loader,
    Modal,
    Paper,
    Select,
    Stack,
    Switch,
    Text,
    TextInput,
    Title,
} from '@mantine/core'
import {useForm} from '@mantine/form'
import {Link} from '@tanstack/react-router'
import {useEffect, useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'
import {useActiveServiceTypes} from 'src/settings/use-service-types'
import {DetailPageBackLink} from 'src/ui/detail-page-back-link'

import {RentalAssetTypeFleetTable} from './rental-asset-type-fleet-table'
import {RentalAssetTypeOfferingsSection} from './rental-asset-type-offerings-section'
import type {RentalAssetType} from './schemas'
import {trackingModeLabel} from './tracking-mode-label'
import {useStockSummary} from './use-equipment-stock'
import {useEquipmentTypeFleet} from './use-equipment-type-fleet'
import {
    useArchiveRentalAssetType,
    useRentalAssetType,
    useUnarchiveRentalAssetType,
    useUpdateRentalAssetType,
} from './use-rental-asset-types'

export type RentalAssetTypeDetailsPageState =
    | 'loading'
    | 'error'
    | 'notFound'
    | 'reading'
    | 'editing'
    | 'saving'

type RentalAssetTypeFormValues = {
    name: string
    hasUnitNumbers: boolean
    defaultServiceTypeId: string | null
    defaultSpecificType: string | null
}

function buildRentalAssetTypeFormValues(
    rentalAssetType: RentalAssetType,
): RentalAssetTypeFormValues {
    return {
        name: rentalAssetType.name,
        hasUnitNumbers: rentalAssetType.hasUnitNumbers,
        defaultServiceTypeId: rentalAssetType.defaultServiceTypeId,
        defaultSpecificType: rentalAssetType.defaultSpecificType,
    }
}

type RentalAssetTypeDetailsPageProps = {
    typeId: string
}

function validateRentalAssetTypeForm(values: RentalAssetTypeFormValues) {
    const errors: Partial<Record<keyof RentalAssetTypeFormValues, string>> = {}
    if (!values.name.trim()) {
        errors.name = 'Type name is required'
    }
    return errors
}

function defaultWorkCategoryLabel(
    rentalAssetType: RentalAssetType,
    categories: {id: string; name: string}[],
): string {
    const category = categories.find((option) => option.id === rentalAssetType.defaultServiceTypeId)
    if (!category) {
        return 'None'
    }

    return [category.name, rentalAssetType.defaultSpecificType].filter(Boolean).join(' · ')
}

function isNotFoundError(error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to load rental asset type'
    return message.toLowerCase().includes('not found')
}

export function RentalAssetTypeDetailsPage({typeId}: RentalAssetTypeDetailsPageProps) {
    const user = useCurrentUser()
    const {
        data: rentalAssetType,
        isLoading: typeLoading,
        isError: typeError,
        error: typeErrorObj,
        refetch: refetchType,
    } = useRentalAssetType(typeId)
    const {
        data: stockSummary,
        isLoading: stockLoading,
        isError: stockError,
        error: stockErrorObj,
        refetch: refetchStock,
    } = useStockSummary(typeId, {
        enabled: Boolean(rentalAssetType && !rentalAssetType.isUniquelyIdentified),
    })
    const {
        data: fleet,
        isLoading: fleetLoading,
        isError: fleetError,
        error: fleetErrorObj,
        refetch: refetchFleet,
    } = useEquipmentTypeFleet(typeId, {
        enabled: Boolean(rentalAssetType?.isUniquelyIdentified),
    })
    const updateRentalAssetType = useUpdateRentalAssetType()
    const archiveRentalAssetType = useArchiveRentalAssetType()
    const unarchiveRentalAssetType = useUnarchiveRentalAssetType()
    const [pageState, setPageState] = useState<RentalAssetTypeDetailsPageState>('loading')
    const {data: categories = []} = useActiveServiceTypes({
        enabled:
            pageState === 'editing' ||
            pageState === 'saving' ||
            rentalAssetType?.defaultServiceTypeId != null,
    })
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [archiveModalOpen, setArchiveModalOpen] = useState(false)
    const [archiveErrorMessage, setArchiveErrorMessage] = useState<string | null>(null)

    const form = useForm<RentalAssetTypeFormValues>({
        initialValues: {
            name: '',
            hasUnitNumbers: true,
            defaultServiceTypeId: null,
            defaultSpecificType: null,
        },
        validate: validateRentalAssetTypeForm,
    })

    useEffect(() => {
        if (typeLoading) {
            setPageState('loading')
            return
        }

        if (typeError) {
            if (isNotFoundError(typeErrorObj)) {
                setPageState('notFound')
                return
            }

            setErrorMessage(
                typeErrorObj instanceof Error
                    ? typeErrorObj.message
                    : 'Failed to load rental asset type',
            )
            setPageState('error')
            return
        }

        if (rentalAssetType) {
            form.setValues(buildRentalAssetTypeFormValues(rentalAssetType))
            if (pageState === 'loading' || pageState === 'error') {
                setPageState('reading')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- form identity is stable; omit to avoid reload on edit
    }, [rentalAssetType, typeLoading, typeError, typeErrorObj])

    function handleEdit() {
        if (!rentalAssetType) {
            return
        }

        form.setValues(buildRentalAssetTypeFormValues(rentalAssetType))
        setPageState('editing')
    }

    function handleCancel() {
        if (!rentalAssetType) {
            return
        }

        form.setValues(buildRentalAssetTypeFormValues(rentalAssetType))
        setPageState('reading')
    }

    async function handleSave() {
        if (!rentalAssetType) {
            return
        }

        const validation = form.validate()
        if (validation.hasErrors) {
            return
        }

        setPageState('saving')
        setErrorMessage(null)

        try {
            await updateRentalAssetType.mutateAsync({
                id: rentalAssetType.id,
                input: {
                    name: form.values.name.trim(),
                    ...(form.values.hasUnitNumbers === rentalAssetType.hasUnitNumbers
                        ? {}
                        : {hasUnitNumbers: form.values.hasUnitNumbers}),
                    ...(form.values.defaultServiceTypeId === rentalAssetType.defaultServiceTypeId &&
                    form.values.defaultSpecificType === rentalAssetType.defaultSpecificType
                        ? {}
                        : {
                              defaultWorkCategory: {
                                  serviceTypeId: form.values.defaultServiceTypeId,
                                  specificType: form.values.defaultSpecificType,
                              },
                          }),
                },
            })
            setPageState('reading')
        } catch (saveError) {
            const message =
                saveError instanceof Error ? saveError.message : 'Failed to save rental asset type'
            setErrorMessage(message)
            setPageState('editing')
        }
    }

    async function handleArchiveConfirm() {
        if (!rentalAssetType) {
            return
        }
        setArchiveErrorMessage(null)
        try {
            await archiveRentalAssetType.mutateAsync(rentalAssetType.id)
            setArchiveModalOpen(false)
        } catch (archiveError) {
            const message =
                archiveError instanceof Error
                    ? archiveError.message
                    : 'Failed to archive equipment type'
            setArchiveErrorMessage(message)
        }
    }

    async function handleUnarchive() {
        if (!rentalAssetType) {
            return
        }
        try {
            await unarchiveRentalAssetType.mutateAsync(rentalAssetType.id)
        } catch (unarchiveError) {
            setErrorMessage(
                unarchiveError instanceof Error
                    ? unarchiveError.message
                    : 'Failed to unarchive equipment type',
            )
        }
    }

    const stockErrorMessage =
        stockError && stockErrorObj instanceof Error
            ? stockErrorObj.message
            : stockError
              ? 'Failed to load stock summary'
              : null

    const fleetErrorMessage =
        fleetError && fleetErrorObj instanceof Error
            ? fleetErrorObj.message
            : fleetError
              ? 'Failed to load fleet'
              : null

    if (pageState === 'loading') {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        )
    }

    if (pageState === 'notFound') {
        return (
            <Stack gap="md">
                <Title order={1}>Equipment type not found</Title>
                <Text c="dimmed">This type may have been removed or the link is invalid.</Text>
                <DetailPageBackLink to="/rental-assets/types" listLabel="equipment types" />
            </Stack>
        )
    }

    if (pageState === 'error' || !rentalAssetType) {
        return (
            <Stack gap="md">
                <Title order={1}>Equipment type details</Title>
                <Alert color="red" title="Could not load equipment type">
                    {errorMessage ?? 'Something went wrong.'}
                </Alert>
                <Button onClick={() => void refetchType()} variant="light">
                    Retry
                </Button>
            </Stack>
        )
    }

    const isEditing = pageState === 'editing' || pageState === 'saving'
    const specificTypes =
        categories.find((category) => category.id === form.values.defaultServiceTypeId)
            ?.specificTypes ?? []

    return (
        <Stack gap="md">
            <Stack gap={4}>
                <DetailPageBackLink to="/rental-assets/types" listLabel="equipment types" />

                <Group justify="space-between" align="flex-start">
                    <Title order={1}>{rentalAssetType.name}</Title>

                    {pageState === 'reading' && isAdmin(user) && (
                        <Group gap="sm">
                            <Button onClick={handleEdit}>Edit</Button>
                            {rentalAssetType.archivedAt == null ? (
                                <Button
                                    variant="subtle"
                                    color="red"
                                    onClick={() => {
                                        setArchiveErrorMessage(null)
                                        setArchiveModalOpen(true)
                                    }}
                                >
                                    Archive
                                </Button>
                            ) : (
                                <Button variant="subtle" onClick={() => void handleUnarchive()}>
                                    Unarchive
                                </Button>
                            )}
                        </Group>
                    )}

                    {isEditing && (
                        <Group gap="sm">
                            <Button
                                variant="default"
                                onClick={handleCancel}
                                disabled={pageState === 'saving'}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => void handleSave()}
                                loading={pageState === 'saving'}
                            >
                                Save
                            </Button>
                        </Group>
                    )}
                </Group>
            </Stack>

            {errorMessage && isEditing && (
                <Alert color="red" title="Could not save equipment type">
                    {errorMessage}
                </Alert>
            )}

            {pageState === 'reading' ? (
                <>
                    <Paper withBorder p="md" radius="md">
                        <Stack gap="sm">
                            <div>
                                <Text size="sm" c="dimmed">
                                    Name
                                </Text>
                                <Text>{rentalAssetType.name}</Text>
                            </div>
                            <div>
                                <Text size="sm" c="dimmed">
                                    Tracking
                                </Text>
                                <Text>
                                    {trackingModeLabel(rentalAssetType.isUniquelyIdentified)}
                                </Text>
                            </div>
                            <div>
                                <Text size="sm" c="dimmed">
                                    Default work category
                                </Text>
                                <Text>{defaultWorkCategoryLabel(rentalAssetType, categories)}</Text>
                            </div>
                            <div>
                                <Text size="sm" c="dimmed">
                                    Re-rented units have unit numbers
                                </Text>
                                <Text>{rentalAssetType.hasUnitNumbers ? 'Yes' : 'No'}</Text>
                            </div>
                        </Stack>
                    </Paper>

                    {rentalAssetType.isUniquelyIdentified ? (
                        <RentalAssetTypeFleetTable
                            fleet={fleet}
                            isLoading={fleetLoading}
                            isError={fleetError}
                            errorMessage={fleetErrorMessage}
                            onRetry={() => void refetchFleet()}
                        />
                    ) : (
                        <Paper withBorder p="md" radius="md">
                            <Stack gap="sm">
                                <Title order={4}>Stock level</Title>

                                {stockLoading ? (
                                    <Text size="sm" c="dimmed">
                                        Loading...
                                    </Text>
                                ) : stockErrorMessage ? (
                                    <Stack gap="sm">
                                        <Alert color="red" title="Could not load stock summary">
                                            {stockErrorMessage}
                                        </Alert>
                                        <Button
                                            onClick={() => void refetchStock()}
                                            variant="light"
                                            size="compact-sm"
                                        >
                                            Retry
                                        </Button>
                                    </Stack>
                                ) : (
                                    <>
                                        <Text>
                                            Total stock level: {stockSummary?.totalCount ?? 0}
                                        </Text>
                                        <Anchor
                                            component={Link}
                                            to="/rental-assets/stock/$typeId"
                                            params={{typeId: rentalAssetType.id}}
                                        >
                                            View overview
                                        </Anchor>
                                    </>
                                )}
                            </Stack>
                        </Paper>
                    )}

                    <RentalAssetTypeOfferingsSection rentalAssetType={rentalAssetType} />
                </>
            ) : (
                <Paper withBorder p="md" radius="md">
                    <Stack gap="md">
                        <TextInput label="Name" withAsterisk {...form.getInputProps('name')} />
                        <Switch
                            label="Re-rented units have unit numbers"
                            description="Turn off for unnumbered items like hoses or mats."
                            {...form.getInputProps('hasUnitNumbers', {type: 'checkbox'})}
                        />
                        <Select
                            label="Default work category"
                            description="Default for jobs renting this type"
                            placeholder="None"
                            clearable
                            data={categories.map((category) => ({
                                value: category.id,
                                label: category.name,
                            }))}
                            value={form.values.defaultServiceTypeId}
                            onChange={(value) =>
                                form.setValues({
                                    defaultServiceTypeId: value,
                                    defaultSpecificType: null,
                                })
                            }
                        />
                        {specificTypes.length > 0 ? (
                            <Select
                                label="Default specific type"
                                placeholder="None"
                                clearable
                                data={specificTypes}
                                {...form.getInputProps('defaultSpecificType')}
                            />
                        ) : null}
                    </Stack>
                </Paper>
            )}

            <Modal
                opened={archiveModalOpen}
                onClose={() => setArchiveModalOpen(false)}
                title="Archive equipment type"
                centered
            >
                <Stack gap="md">
                    {archiveErrorMessage && (
                        <Alert color="red" title="Could not archive equipment type">
                            {archiveErrorMessage}
                        </Alert>
                    )}
                    <Text>
                        Are you sure you want to archive {rentalAssetType.name}? Archived equipment
                        types cannot be used for new assets, offerings, or job requirements.
                    </Text>
                    <Group justify="flex-end">
                        <Button
                            variant="subtle"
                            onClick={() => setArchiveModalOpen(false)}
                            disabled={archiveRentalAssetType.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            loading={archiveRentalAssetType.isPending}
                            onClick={() => void handleArchiveConfirm()}
                        >
                            Archive
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    )
}
